import React from 'react';
import { PillToggle, Text } from '@chatsdet/ui';
import type { VisualCheckpointDetail } from '@chatsdet/types';
import { IMG_CLASS, Pane } from '../../../features/visual/VisualPane';

type ViewerMode = 'side-by-side' | 'swipe' | 'highlight';

const MODE_OPTIONS = [
  { value: 'side-by-side', label: 'Side by side' },
  { value: 'swipe', label: 'Swipe' },
  { value: 'highlight', label: 'Highlight' },
] as const;

/** Swipe: the baseline is clipped over the current image at the slider position. */
function SwipeView({ baselineUrl, currentUrl }: { baselineUrl: string; currentUrl: string }) {
  const [position, setPosition] = React.useState(50);
  return (
    <div className="space-y-3">
      <div className="relative">
        <img src={currentUrl} alt="Current screenshot" className={IMG_CLASS} />
        <img
          src={baselineUrl}
          alt="Baseline screenshot"
          className={`${IMG_CLASS} absolute inset-0`}
          style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
        />
        <div
          className="pointer-events-none absolute inset-y-0 w-0.5 bg-primary"
          style={{ left: `${position}%` }}
        />
      </div>
      <input
        type="range"
        min={0}
        max={100}
        value={position}
        onChange={(e) => setPosition(Number(e.target.value))}
        aria-label="Swipe between baseline and current"
        className="w-full"
      />
      <Text size="xs" className="text-muted-foreground">
        Baseline on the left, current on the right.
      </Text>
    </div>
  );
}

/** Highlight: diff regions (current-image pixels) drawn as percentage-positioned rectangles. */
function HighlightView({ detail, currentUrl }: { detail: VisualCheckpointDetail; currentUrl: string }) {
  const [natural, setNatural] = React.useState<{ width: number; height: number } | null>(null);
  const width = detail.width ?? natural?.width ?? null;
  const height = detail.height ?? natural?.height ?? null;
  return (
    <div className="relative">
      <img
        src={currentUrl}
        alt="Current screenshot"
        className={IMG_CLASS}
        onLoad={(e) => setNatural({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })}
      />
      {width && height
        ? detail.regions.map((r, i) => (
            <div
              key={i}
              data-testid="visual-region"
              className="pointer-events-none absolute border-2 border-red-500 bg-red-500/15"
              style={{
                left: `${(r.x / width) * 100}%`,
                top: `${(r.y / height) * 100}%`,
                width: `${(r.width / width) * 100}%`,
                height: `${(r.height / height) * 100}%`,
              }}
            />
          ))
        : null}
    </div>
  );
}

export function VisualImageViewer({ detail }: { detail: VisualCheckpointDetail }) {
  const [mode, setMode] = React.useState<ViewerMode>('side-by-side');
  const { baselineUrl, imageUrl: currentUrl, diffUrl } = detail;
  const needsBoth = (mode === 'swipe' && !(baselineUrl && currentUrl)) || (mode === 'highlight' && !currentUrl);

  return (
    <div className="space-y-4">
      <PillToggle value={mode} onChange={setMode} options={MODE_OPTIONS} aria-label="Comparison view" />
      {mode === 'side-by-side' && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Pane title="Baseline" url={baselineUrl} missing="No baseline yet" />
          <Pane title="Current" url={currentUrl} missing="No capture" />
          <Pane title="Diff" url={diffUrl} missing="No diff" />
        </div>
      )}
      {mode === 'swipe' && baselineUrl && currentUrl && (
        <SwipeView baselineUrl={baselineUrl} currentUrl={currentUrl} />
      )}
      {mode === 'highlight' && currentUrl && <HighlightView detail={detail} currentUrl={currentUrl} />}
      {needsBoth && (
        <Text size="sm" className="text-muted-foreground">
          {mode === 'swipe' ? 'Swipe needs both a baseline and a current capture.' : 'No capture to highlight.'}
        </Text>
      )}
    </div>
  );
}
